export interface CheckTemplate {
  name: string
  customerId: number
  vehicleIds: number[]
  intervals: CheckTemplateInterval[]
  vehicleServices: CheckTemplateVehicleService[]
}

export interface CheckTemplateListDto {
  id: number
  name: string
  customerId: number
  customerName: string
  vehicleCount: number
  intervalCount: number
  vehicleIds: number[]
  checkTemplateIntervals: CheckTemplateIntervalDto[]
  createdTime?: string
  updatedTime?: string
}

export type CheckTemplateCreate = Omit<CheckTemplate, "customerId">

export interface CheckTemplateUpdate {
  id: number
  name: string
  intervals: CheckTemplateInterval[]
  vehicleIds: number[]
  vehicleServices: CheckTemplateVehicleService[]
}

export interface CheckTemplateInterval {
  type: CheckTemplateIntervalType
  value?: number
  sourceType: CheckTemplateSourceType
  sourceId?: number
  calculationType: IntervalCalculationType
}

export interface CheckTemplateIntervalDto extends CheckTemplateInterval {
  id: number
  checkTemplateId: number
  typeDescription: string
  sourceTypeDescription: string
  calculationTypeDescription: string
  createdById?: number
  createdByName: string
  createdTime?: string
  updatedById?: number
  updatedByName: string
  updatedTime?: string
  vehicleServices: CheckTemplateVehicleServiceDto[]
}

export enum CheckTemplateSourceType {
  Manual = 1,
  Automatic = 2,
  IoT = 3,
  TransportStyrelsen = 4
}

export enum IntervalCalculationType {
  Dynamic = 1,
  Static = 2
}

export interface CheckTemplateVehicleService {
  vehicleId: number
  lastCompletedValue: string
    // nextValue: string
}

export enum CheckTemplateIntervalType {
  Days = 1,
  Distance = 2
}

export interface CheckTemplateVehicleServiceDto {
  id: number
  checkTemplateIntervalId: number
  vehicleId: number
  vehicleRegNumber: string
  vehicleInternNumber: string
  checkTemplateName: string
  nextValue: string
  lastCompletedValue: string
  lastCompletedDate: string | null
  lastCompletedDistance: number | null
  intervalType: string
  intervalValue: number
  sourceType: string
  serviceType: string
  isOverdue: boolean
}
// Base interface for all vehicle checks (services and inspections)
export interface VehicleCheck {
  vehicleId: number
  vehicleRegNumber: string
  vehicleInternNumber: string
  checkTemplateName: string
  checkTemplateId: number

  // Date-based information (shared by both services and inspections)
  nextServiceDate?: string
  lastCompletedDate?: string
  daysUntilService?: number
  isDateOverdue?: boolean

  // Common status
  isOverdue: boolean
  status: string // "OK", "Due Soon", "Overdue"
  earliestDueDate?: string
}

// Service-specific interface extending the base check
export interface VehicleService extends VehicleCheck {
  // Interval information specific to services
  intervalType?: string // "Days" or "Distance"
  intervalValue?: number

  // Distance-based service information (only for services)
  nextServiceDistance?: number
  lastCompletedDistance?: number
  currentOdometerReading?: number
  distanceUntilService?: number
  isDistanceOverdue?: boolean

  // Service intervals data
  services?: Array<{
      id: number
      intervalType: string
      intervalValue: number
  }>
}

// Inspection-specific interface extending the base check
export interface VehicleInspection extends VehicleCheck {
  // Inspection booking information
  hasInspectionBooking?: boolean
  inspectionBookingId?: number
  inspectionBookingDate?: string
  inspectionBookingRemark?: string
}

// Union type for when we need to handle both
export type VehicleUpcomingCheck = VehicleService | VehicleInspection

// Type guards to help distinguish between services and inspections
export function isVehicleService(check: VehicleUpcomingCheck): check is VehicleService {
  return 'intervalType' in check || 'distanceUntilService' in check
}

export function isVehicleInspection(check: VehicleUpcomingCheck): check is VehicleInspection {
  return 'hasInspectionBooking' in check || 'inspectionBookingId' in check
}
